import { registerFunctionalComponent } from 'js/lib/components/component'
import { openCloudinaryWidget, isCloudinaryWidgetOpen, preloadCloudinaryWidget } from 'js/lib/cloudinary'
import { on } from 'js/lib/events'

registerFunctionalComponent('.js-c-img-upload-input', 'ImageUploader', ImageUploader)

function ImageUploader(component) {
  preloadCloudinaryWidget()

  component.useHandler(() => {
    const button = component.element.querySelector('.js-c-img-upload-input__button')
    if (!button) return () => {}

    return on('click', button, event => {
      event.preventDefault()
      handleImageUpload(component.element)
    })
  })

  component.useHandler(() => {
    const button = component.element.querySelector('.js-c-img-upload-input__remove-button')
    if (!button) return () => {}

    return on('click', button, event => {
      event.preventDefault()
      removeImage(component.element)
    })
  })
}

async function handleImageUpload(element) {
  if (isCloudinaryWidgetOpen()) return

  const { tag, allowedFormats, maxImageWidth, maxImageHeight, minImageWidth, minImageHeight, maxFileSize } = JSON.parse(
    element.dataset.clientState
  )

  const errorEl = element.querySelector('.js-c-img-upload-input__error')

  // Clear any previous error when re-opening the widget
  if (errorEl) {
    errorEl.textContent = ''
    errorEl.classList.add('tw-hidden')
  }

  let results

  try {
    results = await openCloudinaryWidget({
      uploadPreset: 'no_transforms',
      tags: [tag],
      multiple: false,
      sources: ['local', 'url'],
      maxFileSize,
      resourceType: 'image',
      allowedFormats,
      maxImageWidth,
      maxImageHeight,
      minImageWidth,
      minImageHeight,
    })
  } catch {
    if (errorEl) {
      errorEl.textContent = 'Image upload failed. Please try again.'
      errorEl.classList.remove('tw-hidden')
    }
    return
  }

  if (!results.length) {
    if (errorEl) {
      const hiddenInput = element.querySelector('.js-c-img-upload-input__hidden-input')
      // Only show the error if no image has been previously uploaded
      if (!hiddenInput || !hiddenInput.value) {
        errorEl.textContent = 'Image could not be uploaded. Please check the file meets the requirements and try again.'
        errorEl.classList.remove('tw-hidden')
      }
    }
    return
  }

  // Upload succeeded — clear any error
  if (errorEl) {
    errorEl.textContent = ''
    errorEl.classList.add('tw-hidden')
  }

  const info = results[0].info
  const imageUrl = info.secure_url

  const thumbnailUrl = imageUrl.replace('/upload/', '/upload/h_176,c_fit,q_auto,f_auto/')

  const thumbnailContainer = thumbnailElement(element)
  const hiddenInput = element.querySelector('.js-c-img-upload-input__hidden-input')

  removeClearInput(element)
  element.dataset.currentImageIsNew = 'true'
  thumbnailContainer.classList.remove('hidden')
  thumbnailContainer.classList.add('tw-animate-pulse', 'tw-bg-neutral-100')

  const tempImg = new Image()
  tempImg.onload = () => {
    updateThumbnail(element, thumbnailUrl)
    thumbnailContainer.classList.remove('tw-animate-pulse', 'tw-bg-neutral-100')
  }
  tempImg.onerror = () => {
    updateThumbnail(element, imageUrl)
    thumbnailContainer.classList.remove('tw-animate-pulse', 'tw-bg-neutral-100')
  }
  tempImg.src = thumbnailUrl

  hiddenInput.value = imageUrl
  showRemoveButton(element)
}

function removeImage(element) {
  const state = JSON.parse(element.dataset.clientState)
  const hiddenInput = element.querySelector('.js-c-img-upload-input__hidden-input')

  if (element.dataset.currentImageIsNew === 'true') {
    hiddenInput.value = state.initialValue || ''
    removeClearInput(element)
    element.dataset.currentImageIsNew = 'false'

    if (state.initialPreviewValue) {
      updateThumbnail(element, state.initialPreviewValue)
      showRemoveButton(element)
    } else {
      hideThumbnail(element)
      hideRemoveButton(element)
    }

    return
  }

  hiddenInput.value = ''
  hideThumbnail(element)
  hideRemoveButton(element)
  element.dataset.currentImageIsNew = 'false'

  if (state.initiallyRemovable && state.removeInputName) addClearInput(element, state.removeInputName, state.removeInputValue)
}

function updateThumbnail(element, imageUrl) {
  const thumbnailContainer = thumbnailElement(element)
  const img = thumbnailContainer.querySelector('img')
  const thumbnailUrl = imageUrl.replace('/upload/', '/upload/h_176,c_fit,q_auto,f_auto/')

  img.src = thumbnailUrl
  thumbnailContainer.classList.remove('tw-hidden', 'hidden')
}

function hideThumbnail(element) {
  thumbnailElement(element).classList.add('tw-hidden')
}

function thumbnailElement(element) {
  return element.querySelector('.js-c-img-upload-input__thumbnail')
}

function showRemoveButton(element) {
  const button = element.querySelector('.js-c-img-upload-input__remove-button')
  if (!button) return

  button.classList.remove('tw-hidden')
}

function hideRemoveButton(element) {
  const button = element.querySelector('.js-c-img-upload-input__remove-button')
  if (!button) return

  button.classList.add('tw-hidden')
}

function addClearInput(element, name, value) {
  removeClearInput(element)

  const input = document.createElement('input')
  input.type = 'hidden'
  input.name = name
  input.value = value
  input.classList.add('js-c-img-upload-input__clear-input')
  element.append(input)
}

function removeClearInput(element) {
  element.querySelector('.js-c-img-upload-input__clear-input')?.remove()
}
